import { useQuery } from '@tanstack/react-query'
import { ClipboardPen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/errors'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader } from '@/components/ui/Card'
import { formatDate } from '@/utils/format'
import { markSheetQuery, useSaveMarks } from '../api'
import { formatMarks } from '../status'
import { sectionLabel } from '@/features/academic/sectionLabel'
import { MarkEntryGrid } from './MarkEntryGrid'
import { ProgressMeter } from './ProgressMeter'

interface MarkSheetPanelProps {
  examId: number
  paperId: number
  sectionId: number
}

/** Loads one mark sheet and renders the entry grid; re-mounts per paper × section. */
export function MarkSheetPanel({ examId, paperId, sectionId }: MarkSheetPanelProps) {
  const { t } = useTranslation('exams')
  const sheet = useQuery(markSheetQuery(examId, paperId, sectionId))
  const save = useSaveMarks()

  if (sheet.isPending)
    return (
      <Card>
        <LoadingState label={t('Loading mark sheet…')} />
      </Card>
    )
  if (sheet.error) {
    const forbidden = sheet.error instanceof ApiError && sheet.error.status === 403
    return (
      <Card>
        <ErrorState
          title={forbidden ? t('You cannot grade this sheet') : undefined}
          description={
            forbidden
              ? t('Marks can be entered by the class teacher of the section or by teachers of the subject.')
              : undefined
          }
          onRetry={forbidden ? undefined : () => void sheet.refetch()}
        />
      </Card>
    )
  }
  const s = sheet.data
  const entered = s.students.filter((st) => st.isAbsent || st.marksObtained !== null).length

  return (
    <Card>
      <CardHeader
        icon={<ClipboardPen />}
        title={`${s.paper.subjectName} · ${sectionLabel(s.section.className, s.section.name, ' – ')}`}
        description={
          <>
            {t('Out of {{max}} · pass {{pass}}', {
              max: formatMarks(s.paper.maxMarks),
              pass: formatMarks(s.paper.passMarks),
            })}
            {s.paper.date ? ` · ${t('sat on {{date}}', { date: formatDate(s.paper.date) })}` : ''}
          </>
        }
        actions={
          s.canEdit ? (
            <Badge tone="warning" dot>
              {t('Open')}
            </Badge>
          ) : (
            <Badge tone="neutral">{t('Read only')}</Badge>
          )
        }
      />
      <div className="border-b border-line px-5 py-3">
        <ProgressMeter entered={entered} expected={s.students.length} label={t('Section progress')} />
      </div>
      <MarkEntryGrid
        key={`${paperId}-${sectionId}`}
        sheet={s}
        onSave={async (entries) => (await save.mutateAsync({ examId, paperId, sectionId, entries })).sheet}
      />
    </Card>
  )
}
